// Multi-country CJ shipping comparison -- pure state helpers (no React),
// used by ShippingCostCalculator / the Sync & Resync wizard / the import
// page, and tested with node --test (tests/cjShippingDestinations.test.js).
//
// Two independent concepts:
//  - ONE global variant selection ("all" or an explicit list of CJ variant
//    ids) -- the same variants for every destination country, no limit;
//  - up to MAX_DESTINATION_COUNTRIES destination countries, each with its
//    own shipping methods, selected method, cost, delivery time, optional
//    selling price, profit and sellActive flag.
const { calculateCJPricing } = require("./cjPricing");

const MAX_DESTINATION_COUNTRIES = 4;

// Destinations offered in the selector (ISO 3166-1 alpha-2). A plain list
// rather than free typing avoids invalid codes being sent to CJ.
const DESTINATION_COUNTRIES = [
  ["US", "United States"], ["CA", "Canada"], ["MX", "Mexico"], ["GB", "United Kingdom"],
  ["FR", "France"], ["DE", "Germany"], ["ES", "Spain"], ["IT", "Italy"],
  ["NL", "Netherlands"], ["BE", "Belgium"], ["PT", "Portugal"], ["IE", "Ireland"],
  ["CH", "Switzerland"], ["AT", "Austria"], ["SE", "Sweden"], ["NO", "Norway"],
  ["DK", "Denmark"], ["FI", "Finland"], ["PL", "Poland"], ["AU", "Australia"],
  ["NZ", "New Zealand"], ["JP", "Japan"], ["KR", "South Korea"], ["SG", "Singapore"],
  ["BR", "Brazil"], ["CL", "Chile"], ["CO", "Colombia"], ["PE", "Peru"],
  ["AR", "Argentina"], ["DO", "Dominican Republic"], ["HT", "Haiti"], ["JM", "Jamaica"],
  ["PR", "Puerto Rico"], ["PA", "Panama"], ["CR", "Costa Rica"], ["IL", "Israel"],
  ["AE", "United Arab Emirates"], ["SA", "Saudi Arabia"], ["ZA", "South Africa"], ["PH", "Philippines"],
];
const COUNTRY_NAMES = Object.fromEntries(DESTINATION_COUNTRIES);
const countryName = (code) => COUNTRY_NAMES[code] || code;
const isCountryCode = (code) => /^[A-Z]{2}$/.test(String(code || ""));

// ── Countries ─────────────────────────────────────────────────────────
// Each returns { countries, error } -- `countries` unchanged on error.
const addCountry = (countries, code) => {
  const next = String(code || "").toUpperCase();
  if (!isCountryCode(next)) return { countries, error: "INVALID_COUNTRY" };
  if (countries.includes(next)) return { countries, error: "DUPLICATE_COUNTRY" };
  if (countries.length >= MAX_DESTINATION_COUNTRIES) return { countries, error: "TOO_MANY_COUNTRIES" };
  return { countries: [...countries, next], error: null };
};
const removeCountry = (countries, code) => {
  if (countries.length <= 1) return { countries, error: "COUNTRIES_REQUIRED" };
  return { countries: countries.filter((c) => c !== code), error: null };
};
const replaceCountry = (countries, oldCode, newCode) => {
  const next = String(newCode || "").toUpperCase();
  if (!isCountryCode(next)) return { countries, error: "INVALID_COUNTRY" };
  if (next === oldCode) return { countries, error: null };
  if (countries.includes(next)) return { countries, error: "DUPLICATE_COUNTRY" };
  return { countries: countries.map((c) => (c === oldCode ? next : c)), error: null };
};

// ── Global variant selection ──────────────────────────────────────────
// { mode: "all" } or { mode: "selected", ids: [...] }. "all" really means
// every variant of the product (the complete list from the API, not a page).
const variantIdsOf = (variants) => (Array.isArray(variants) ? variants : []).map((v) => String(v?.externalVariantId || "")).filter(Boolean);
const selectedVariantIds = (selection, variants) => {
  const all = variantIdsOf(variants);
  if (!selection || selection.mode === "all") return all;
  const wanted = new Set(selection.ids || []);
  return all.filter((id) => wanted.has(id));
};
const isVariantSelected = (selection, id) => !selection || selection.mode === "all" || (selection.ids || []).includes(id);
const toggleVariant = (selection, id, variants) => {
  const current = selectedVariantIds(selection, variants);
  const next = current.includes(id) ? current.filter((v) => v !== id) : [...current, id];
  const all = variantIdsOf(variants);
  return next.length === all.length && all.length > 0 ? { mode: "all" } : { mode: "selected", ids: next };
};
const selectAllVariants = () => ({ mode: "all" });
const clearVariants = () => ({ mode: "selected", ids: [] });
// What is sent to the server ({mode:"all"} keeps following CJ's list).
const variantSelectionPayload = (selection, variants) => (
  !selection || selection.mode === "all"
    ? { mode: "all" }
    : { mode: "selected", selectedExternalVariantIds: selectedVariantIds(selection, variants) }
);
// Stable signature: a result computed for another variant set or quantity is
// stale and never reused silently.
const quoteSignature = (selection, variants, quantity) =>
  `${selectedVariantIds(selection, variants).slice().sort().join(",")}|${Number(quantity) || 1}`;

// Highest CJ cost among the selected variants (profit is never overstated
// for any selected variant).
const selectedVariantsCost = (selection, variants, fallback = 0) => {
  const ids = new Set(selectedVariantIds(selection, variants));
  const costs = (variants || []).filter((v) => ids.has(String(v.externalVariantId))).map((v) => Number(v.costPrice) || 0).filter((c) => c > 0);
  return costs.length ? Math.max(...costs) : (Number(fallback) || 0);
};

// ── Per-country shipping + pricing ────────────────────────────────────
const selectedOption = (countryState) => {
  const options = countryState?.options || [];
  return options.find((o) => o.carrier === countryState?.selectedCarrier) || null;
};
// Default method inside ONE country: cheapest available for every selected
// variant, else the first option.
const defaultCarrier = (options) => {
  const list = Array.isArray(options) ? options : [];
  const eligible = list.filter((o) => o.availableForAllVariants !== false && Number(o.price) > 0);
  const cheapest = eligible.reduce((best, o) => (!best || o.price < best.price ? o : best), null);
  return (cheapest || list[0] || null)?.carrier || "";
};

// Profit for ONE country with the shared engine -- the effective selling
// price is the country override, else the product's default price.
const computeCountryPricing = ({
  shippingCost, productCost, shippingMode, discountPercent, taxRate,
  defaultSellingPrice, sellingPriceOverride, desiredProfit,
}) => {
  const S = shippingMode === "include" ? 0 : shippingCost;
  const override = Number(sellingPriceOverride);
  const effectiveSellingPrice = override > 0 ? override : Number(defaultSellingPrice) || 0;
  const pricing = effectiveSellingPrice > 0 ? calculateCJPricing({
    mode: "profit",
    cjProductCost: Number(productCost) || 0,
    cjShippingCost: Number(shippingCost) || 0,
    shippingChargedToCustomer: S,
    discountPercent: Number(discountPercent) || 0,
    sellingPrice: effectiveSellingPrice,
    taxRate,
  }) : null;
  const required = desiredProfit !== undefined && desiredProfit !== null && desiredProfit !== "" ? calculateCJPricing({
    mode: "sellingPrice",
    cjProductCost: Number(productCost) || 0,
    cjShippingCost: Number(shippingCost) || 0,
    shippingChargedToCustomer: S,
    discountPercent: Number(discountPercent) || 0,
    desiredProfit: Math.abs(Number(desiredProfit) || 0),
    taxRate,
  }) : null;
  return {
    effectiveSellingPrice,
    usesOverride: override > 0,
    pricing,
    requiredSellingPrice: required ? required.sellingPrice : null,
  };
};

// Server payload (PATCH .../pricing dropshippingData.shippingDestinations):
// only countries with a real selected method; a failed country is never
// persisted (never overwrites a valid result with an invalid one).
const buildShippingDestinationsPayload = ({ countries, perCountry, quantity, computed }) => countries
  .map((code) => {
    const state = perCountry[code];
    const option = selectedOption(state);
    if (!state || state.status !== "success" || !option || state.stale) return null;
    const result = computed?.[code];
    const override = Number(state.sellingPriceOverride);
    return {
      countryCode: code,
      countryName: countryName(code),
      carrier: option.carrier,
      carrierCode: option.carrierCode || "",
      carrierId: option.carrierId || "",
      quotedVariantIds: Array.isArray(state.quotedVariantIds) ? state.quotedVariantIds : [],
      quoteSource: "CJ",
      shippingCost: Number(option.price) || 0,
      shippingCostMin: Number(option.priceMin ?? option.price) || 0,
      currency: "USD",
      deliveryDays: option.deliveryDays || "",
      quantity: Number(quantity) || 1,
      priceCny: Number(option.priceCny) || 0,
      taxesFee: Number(option.taxesFee) || 0,
      clearanceOperationFee: Number(option.clearanceOperationFee) || 0,
      totalPostageFee: Number(option.totalPostageFee) || 0,
      sellingPrice: override > 0 ? Number(override.toFixed(2)) : null,
      ...(result?.pricing ? {
        calculatedProfit: result.pricing.profit,
        profitMargin: result.pricing.sellingPrice > 0 ? Number(((result.pricing.profit / result.pricing.sellingPrice) * 100).toFixed(2)) : 0,
      } : {}),
      sellActive: state.sellActive !== false,
      lastCalculatedAt: state.calculatedAt || null,
    };
  })
  .filter(Boolean);

// Prefill from a product already synchronized (Resync): the saved countries
// (or the legacy single quote's country) and the saved variant selection.
const initialStateFromProduct = (dropshippingData) => {
  const data = dropshippingData || {};
  const saved = Array.isArray(data.shippingDestinations) ? data.shippingDestinations.filter((d) => isCountryCode(d?.countryCode)) : [];
  const legacyCountry = String(data.shippingQuote?.destinationCountry || "").toUpperCase();
  const countries = saved.length
    ? saved.map((d) => d.countryCode).slice(0, MAX_DESTINATION_COUNTRIES)
    : (isCountryCode(legacyCountry) ? [legacyCountry] : ["US"]);
  const overrides = {};
  const sellActive = {};
  const carriers = {};
  saved.forEach((d) => {
    if (Number(d.sellingPrice) > 0) overrides[d.countryCode] = String(d.sellingPrice);
    sellActive[d.countryCode] = d.sellActive !== false;
    if (d.carrier) carriers[d.countryCode] = d.carrier;
  });
  if (!saved.length && legacyCountry && data.shippingQuote?.carrier) carriers[legacyCountry] = data.shippingQuote.carrier;
  const sel = data.variantSelection || {};
  let variantSelection = { mode: "all" };
  if (sel.mode === "selected" && Array.isArray(sel.selectedExternalVariantIds) && sel.selectedExternalVariantIds.length) {
    variantSelection = { mode: "selected", ids: sel.selectedExternalVariantIds.map(String) };
  } else if ((!sel.mode || sel.mode === "legacy") && Array.isArray(data.excludedVariantExternalIds) && data.excludedVariantExternalIds.length) {
    // Legacy exclusions -> resolved into an explicit list once the complete
    // variant list is known (see resolveLegacySelection).
    variantSelection = { mode: "legacyExcluded", excluded: data.excludedVariantExternalIds.map(String) };
  }
  return { countries, overrides, sellActive, carriers, variantSelection };
};
const resolveLegacySelection = (selection, variants) => {
  if (selection?.mode !== "legacyExcluded") return selection;
  const excluded = new Set(selection.excluded || []);
  const ids = variantIdsOf(variants).filter((id) => !excluded.has(id));
  return ids.length === variantIdsOf(variants).length ? { mode: "all" } : { mode: "selected", ids };
};

module.exports = {
  MAX_DESTINATION_COUNTRIES,
  DESTINATION_COUNTRIES,
  countryName,
  addCountry,
  removeCountry,
  replaceCountry,
  variantIdsOf,
  selectedVariantIds,
  isVariantSelected,
  toggleVariant,
  selectAllVariants,
  clearVariants,
  variantSelectionPayload,
  quoteSignature,
  selectedVariantsCost,
  selectedOption,
  defaultCarrier,
  computeCountryPricing,
  buildShippingDestinationsPayload,
  initialStateFromProduct,
  resolveLegacySelection,
};
